/**
 * Credits Progress Hook
 *
 * Fetches the credits redemption config (backoffice-configurable) and the
 * authenticated user's credit balance, then computes progress state relative
 * to the current cart subtotal.
 *
 * The bar is hidden when:
 *  - The config has `enabled: false` (backoffice toggle)
 *  - The user is not logged in (no userId)
 *  - Either fetch is still loading
 *  - The user has no credits to spend (balance ≤ 0), including when balance fetch returns null
 *
 * When `cartTotal >= unlock_threshold_cents` the credits are "unlocked" and
 * the customer can apply them at checkout.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getCreditsConfig, getUserCreditBalance } from '@/infrastructure/services/bff/credits.bff-service';

export interface CreditsProgressState {
  /** True when the feature is enabled and the user is logged in. */
  isEnabled: boolean;
  /** Number of whole credits the user holds. 1 credit = $1.00. */
  balance: number;
  /** Minimum cart subtotal in cents needed to unlock credits. */
  unlockThreshold: number;
  /** True when cartTotal >= unlockThreshold (credits ready to use at checkout). */
  isUnlocked: boolean;
  /** Progress toward unlock threshold, 0–100. */
  percentage: number;
  /** Cents still needed to reach the unlock threshold. 0 when already unlocked. */
  remaining: number;
  /** Max combined discount % (credits + coupon). Passed to checkout simulate. */
  maxPercentageDiscount: number;
  /** True while either fetch is in-flight. */
  isLoading: boolean;
}

const DISABLED_STATE: CreditsProgressState = {
  isEnabled: false,
  balance: 0,
  unlockThreshold: 0,
  isUnlocked: false,
  percentage: 0,
  remaining: 0,
  maxPercentageDiscount: 50,
  isLoading: false,
};

/**
 * @param cartTotal    - current cart subtotal in cents (display currency)
 * @param userId       - authenticated user UUID; null when logged out
 * @param currencyCode - ISO 4217 code (e.g. "USD")
 */
export function useCreditsProgress(
  cartTotal: number,
  userId: string | null,
  currencyCode: string,
): CreditsProgressState {
  const { data: config, isLoading: configLoading } = useQuery({
    queryKey: ['credits-config', currencyCode],
    queryFn: () => getCreditsConfig(currencyCode),
    staleTime: 10 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
    enabled: currencyCode.length > 0,
  });

  const { data: balanceData, isLoading: balanceLoading } = useQuery({
    queryKey: ['credits-balance', userId],
    queryFn: () => getUserCreditBalance(userId!),
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
    enabled: userId !== null && userId.length > 0,
  });

  return useMemo(() => {
    const isLoading = configLoading || (userId != null && balanceLoading);

    if (isLoading) {
      return { ...DISABLED_STATE, isLoading: true };
    }

    if (!config?.enabled || userId == null) {
      return DISABLED_STATE;
    }

    const balance = balanceData?.balance ?? 0;
    const threshold = config.unlock_threshold_cents;
    const maxPercentageDiscount = config.max_percentage_discount ?? 50;

    // No usable credits means no bar (avoids "$0.00" copy in progress/unlocked states)
    if (balance <= 0) {
      return DISABLED_STATE;
    }

    // No threshold means no bar (treat as disabled)
    if (threshold <= 0) {
      return DISABLED_STATE;
    }

    const percentage = Math.min((cartTotal / threshold) * 100, 100);
    const remaining = Math.max(threshold - cartTotal, 0);
    const isUnlocked = cartTotal >= threshold;

    return {
      isEnabled: true,
      balance,
      unlockThreshold: threshold,
      isUnlocked,
      percentage,
      remaining,
      maxPercentageDiscount,
      isLoading: false,
    };
  }, [cartTotal, config, balanceData, configLoading, balanceLoading, userId]);
}
